// ============================================================================
// Centralized date/time utilities — the ONE place the SPA turns timestamps into
// human-readable text. Monday-start weeks.
// ----------------------------------------------------------------------------
// TIMEZONE CONVENTION (must be applied consistently across the app):
//   • Timestamps are STORED as timezone-aware UTC ISO-8601 in the DB.
//   • The API accepts client datetimes and normalizes them to UTC.
//   • This SPA PARSES UTC and DISPLAYS in the DEVICE's local timezone, using
//     locale-aware formatting tied to the ACTIVE i18n locale.
//
// So: never display a raw UTC hour, and never format month/weekday names from a
// hardcoded English array. Parse the stored UTC string with `new Date(iso)`
// (the JS engine converts it to the device's local wall-clock) and render with
// `Intl.DateTimeFormat(intlLocale(), …)`. That makes a HK trainer (UTC+8) see
// the correct local day/time, and month/weekday names localize under 繁中.
//
// The grid helpers below operate on local `Date` objects (the calendar lays out
// the trainer's local days); the formatters take either a `Date` or an ISO
// string. Calendar buckets key on the LOCAL date (see dashboard view-model),
// so a session always lands on the day the trainer actually sees it.
// ============================================================================
import { intlLocale } from "../i18n/config";

// --- locale-aware Intl formatter cache --------------------------------------
// Intl.DateTimeFormat construction isn't free; cache per (locale, optionsKey).
const fmtCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = intlLocale();
  const key = locale + "|" + JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    fmtCache.set(key, f);
  }
  return f;
}

/** Coerce an ISO string (UTC or local) or a Date into a valid Date, or null. */
function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ---------------------------------------------------------------------------
// Pure calendar grid math (timezone-agnostic; operate on local Dates)
// ---------------------------------------------------------------------------

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function addDays(d: Date, days: number): Date {
  const x = startOfDay(d);
  x.setDate(x.getDate() + days);
  return x;
}

export function addMonths(d: Date, months: number): Date {
  const x = startOfDay(d);
  x.setDate(1);
  x.setMonth(x.getMonth() + months);
  return x;
}

/** Monday-start week index, 0 (Mon) … 6 (Sun). */
export function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/** The Monday that begins the week containing `d`. */
export function startOfWeek(d: Date): Date {
  return addDays(d, -mondayIndex(d));
}

/**
 * A 6×7 grid of days covering the month containing `ref`, padded with
 * leading/trailing days from adjacent months so every cell is a real date.
 */
export function monthGrid(ref: Date): Date[] {
  const first = startOfDay(new Date(ref.getFullYear(), ref.getMonth(), 1));
  const gridStart = startOfWeek(first);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  return days;
}

/** The seven Monday-start days of the week containing `ref`. */
export function weekDays(ref: Date): Date[] {
  const start = startOfWeek(ref);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Local `YYYY-MM-DD` key for a Date (used to bucket sessions by local day). */
export function localDayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// Locale-aware display formatters (active i18n locale; device-local timezone)
// ---------------------------------------------------------------------------

/**
 * Short localized weekday labels in Monday-start order, e.g.
 * ["Mon".."Sun"] / ["週一".."週日"]. Used for the calendar's weekday header.
 * Derived from Intl so they localize under 繁中 instead of staying English.
 */
export function weekdayLabels(): string[] {
  const f = getFormatter({ weekday: "short" });
  // 2024-01-01 is a Monday — walk 7 days from it.
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: 7 }, (_, i) =>
    f.format(addDays(monday, i)),
  );
}

/** The Monday-start short weekday label for a specific date. */
export function weekdayLabel(d: Date): string {
  return getFormatter({ weekday: "short" }).format(d);
}

/**
 * A short local time, e.g. "6:00 AM" / "上午6:00". Accepts a UTC ISO string OR
 * a Date and renders in the device's local timezone + active locale. Returns ""
 * for missing/invalid input (optional-first).
 */
export function formatTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return getFormatter({ hour: "numeric", minute: "2-digit" }).format(d);
}

/**
 * A long localized "weekday, month day", e.g. "Mon, Jun 16" / "6月16日 週一".
 * Accepts a UTC ISO string OR a Date; renders local + active locale.
 */
export function formatDayLong(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return getFormatter({
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** Just the short weekday for a date, e.g. "Mon" / "週一". */
export function formatWeekdayShort(value: string | Date): string {
  const d = toDate(value);
  if (!d) return "";
  return getFormatter({ weekday: "short" }).format(d);
}

/** Month + year title, e.g. "June 2026" / "2026年6月". */
export function monthTitle(d: Date): string {
  return getFormatter({ month: "long", year: "numeric" }).format(d);
}

/** A localized week-range title, e.g. "Jun 16 – 22, 2026" / "6月16日至22日". */
export function weekRangeTitle(d: Date): string {
  const start = startOfWeek(d);
  const end = addDays(start, 6);
  const f = getFormatter({ month: "short", day: "numeric", year: "numeric" });
  // Intl's formatRange gives proper localized ranges with shared-field elision
  // (e.g. "Jun 16 – 22, 2026"). It isn't in this TS lib's DateTimeFormat type,
  // so feature-detect it; fall back to two formatted endpoints otherwise.
  const fr = (f as { formatRange?: (a: Date, b: Date) => string }).formatRange;
  if (typeof fr === "function") return fr.call(f, start, end);
  return `${f.format(start)} – ${f.format(end)}`;
}

// --- relative time ----------------------------------------------------------
// Cache RelativeTimeFormat instances per locale (construction isn't free).
const relCache = new Map<string, Intl.RelativeTimeFormat>();

function getRelFormatter(): Intl.RelativeTimeFormat {
  const locale = intlLocale();
  let f = relCache.get(locale);
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    relCache.set(locale, f);
  }
  return f;
}

/**
 * A localized relative time for a PAST instant, e.g. "12 days ago" / "12 天前"
 * (繁中). Uses `Intl.RelativeTimeFormat` keyed to the active i18n locale, so it
 * localizes for free (no per-string i18n keys). Picks the coarsest sensible
 * unit (minute → hour → day → month) and clamps to the past (we only ever flag
 * stale clients, never future). Returns "" for missing/invalid input.
 *
 * NOTE: distinct from `formatTime`/`formatDateTimeLong` (absolute wall-clock) —
 * this is the "how long since they were last active" recency phrasing.
 */
export function formatRelativeTime(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toDate(value);
  if (!d) return "";
  const f = getRelFormatter();
  // Always render as the past (negative offset → "… ago"); clamp tiny/future
  // deltas to "now" so we never say a stale client is active "in 3 seconds".
  const diffMs = Math.min(0, d.getTime() - now.getTime());
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  const month = Math.round(day / 30);
  if (day <= -30) return f.format(month, "month");
  if (hr <= -24) return f.format(day, "day");
  if (min <= -60) return f.format(hr, "hour");
  if (sec <= -60) return f.format(min, "minute");
  return f.format(0, "second"); // "now" / "just now" (locale-aware)
}

/** "Jun 16, 2:30 PM" — a localized long date+time for a UTC ISO/Date. */
export function formatDateTimeLong(
  value: string | Date | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return "";
  return getFormatter({
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}
